<template>
  <v-card outlined class="pa-5 rp-panel">
    <div class="d-flex flex-wrap align-center mb-3" style="gap: 6px 10px">
      <div class="text-subtitle-1 font-weight-bold">
        {{ isPayout ? $t("finance_record_payout_title") : $t("finance_record_payment_title") }}
      </div>
      <span class="text-caption grey--text text--darken-1">{{ $t("finance_admin_only") }}</span>
      <v-spacer />
      <v-btn-toggle :value="direction" mandatory dense color="primary" @change="$emit('update:direction', $event)">
        <v-btn value="payout" small>{{ $t("finance_toggle_payout") }}</v-btn>
        <v-btn value="payment" small>{{ $t("finance_toggle_payment") }}</v-btn>
      </v-btn-toggle>
    </div>

    <v-form ref="form" v-model="valid" @submit.prevent>
      <date-picker
        v-model="form.range"
        :label="$t('finance_period_label')"
        :rules="isPayout ? [rules.required] : []"
      />

      <!-- Isplata: brojevi dolaze iz obračuna, admin ih ne kuca (princip 5). -->
      <div v-if="isPayout" class="rp-box mb-3">
        <v-progress-linear v-if="loadingOverview" indeterminate class="mb-2" />
        <div class="rp-grid">
          <div>
            <div class="rp-label">{{ $t("finance_net_for_payout") }}</div>
            <div class="rp-big">{{ calcText(calc.net) }}</div>
          </div>
          <div>
            <div class="rp-label">{{ $t("finance_turnover") }}</div>
            <div class="rp-val">{{ calcText(calc.turnover) }}</div>
          </div>
          <div>
            <div class="rp-label">{{ $t("finance_deductions") }}</div>
            <div class="rp-val">{{ calcText(calc.deductions) }}</div>
          </div>
        </div>
        <a v-if="overview" class="rp-link" @click="showBreakdown = !showBreakdown">
          {{ $t("finance_view_breakdown") }}
        </a>
        <statement-breakdown v-if="showBreakdown && overview" :overview="overview" readonly />
      </div>

      <text-field
        v-model="form.amount"
        type="number"
        :label="$t('finance_payment_amount')"
        :suffix="currency"
        :rules="[rules.required, rules.positive]"
        @input="amountTouched = true"
      />
      <template v-if="isPayout && amountDiffers">
        <div class="rp-warning mb-3">{{ $t("finance_amount_differs_warning") }}</div>
        <text-field v-model="form.reason" :label="$t('finance_difference_reason')" :rules="[rules.required]" />
      </template>

      <v-row dense>
        <v-col cols="12" sm="6">
          <date-picker v-model="form.date" single-date :label="$t('finance_date')" :rules="[rules.required]" />
        </v-col>
        <v-col cols="12" sm="6">
          <payment-method-dropdown v-model="form.method" :rules="[rules.required]" />
        </v-col>
      </v-row>
      <text-field v-model="form.reference" :label="$t('finance_reference')" />
    </v-form>

    <primary-button block :loading="saving" :disabled="saving" @click="submit">
      {{ isPayout ? $t("finance_record_payout") : $t("finance_record_payment") }}
    </primary-button>
  </v-card>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import PaymentMethodDropdown from "../fields/PaymentMethodDropdown.vue";
import StatementBreakdown from "../StatementBreakdown.vue";
import { parseOverview } from "../overview";
import { formatMoney, resolvePeriodPreset, toDateParam } from "../constants";

// UUID generisan pri OTVARANJU forme (ne pri kliku) - isti kroz sve pokušaje istog
// unosa (dupli klik / mrežni retry), backend na isti ključ vraća istu transakciju.
const generateIdempotencyKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const TOLERANCE = 0.005;

// Panel "Evidentiraj plaćanje" (admin) - direction: "payout" (Ordera -> restoran, POST /payouts)
// ili "payment" (restoran -> Ordera, POST /platform-payments). Roditelj povećava `resetToken`
// poslije uspješnog snimanja da se forma isprazni i dobije novi idempotency ključ.
export default {
  name: "RecordPaymentPanel",

  components: { PaymentMethodDropdown, StatementBreakdown },

  props: {
    direction: { type: String, default: "payout" },
    restaurantId: { type: [Number, String], default: null },
    saving: { type: Boolean, default: false },
    resetToken: { type: Number, default: 0 },
  },

  data() {
    return {
      valid: true,
      form: this.emptyForm(),
      idempotencyKey: generateIdempotencyKey(),
      overview: null,
      loadingOverview: false,
      showBreakdown: false,
      amountTouched: false,
      requestSeq: 0,
      rules: {
        required: (v) =>
          (v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && !v.length)) ||
          this.$t("finance_required_field"),
        positive: (v) => Number(v) > 0 || this.$t("finance_amount_must_be_positive"),
      },
    };
  },

  computed: {
    isPayout() {
      return this.direction === "payout";
    },
    currency() {
      return this.overview?.currency || this.restaurantCurrency;
    },
    calc() {
      return {
        turnover: this.overview?.turnover ?? null,
        deductions: this.overview?.deductions ?? null,
        net: this.overview?.net ?? null,
      };
    },
    amountDiffers() {
      const net = this.calc.net;
      if (net === null || this.form.amount === "" || this.form.amount === null) return false;
      return Math.abs(Number(this.form.amount) - net) > TOLERANCE;
    },
    period() {
      const [from, to] = this.form.range || [];
      return from && to ? { from, to } : null;
    },
  },

  watch: {
    direction: "reset",
    resetToken: "reset",
    restaurantId: "reset",
    "form.range"() {
      if (this.isPayout) this.loadOverview();
    },
  },

  created() {
    if (this.isPayout) this.loadOverview();
  },

  methods: {
    emptyForm() {
      const last = resolvePeriodPreset("last_month");
      return {
        range: [last.from, last.to],
        amount: "",
        reason: "",
        date: toDateParam(new Date()),
        method: "",
        reference: "",
      };
    },

    reset() {
      this.form = this.emptyForm();
      this.idempotencyKey = generateIdempotencyKey();
      this.overview = null;
      this.showBreakdown = false;
      this.amountTouched = false;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
      if (this.isPayout) this.loadOverview();
    },

    calcText(value) {
      return value === null ? "—" : formatMoney(value, this.currency);
    },

    async loadOverview() {
      if (!this.period || !this.restaurantId) return;
      const seq = ++this.requestSeq;
      this.loadingOverview = true;
      try {
        const res = await restaurantFinanceService.getFinancialOverview(this.restaurantId, this.period);
        if (seq !== this.requestSeq) return;
        this.overview = parseOverview(res.data?.data || res.data || {}, this.restaurantCurrency);
        // Iznos predpopunjen = Neto (princip 5); ne gazimo ga ako ga je admin već mijenjao.
        if (!this.amountTouched && this.overview.net !== null) {
          this.form.amount = this.overview.net > 0 ? this.overview.net.toFixed(2) : "";
        }
      } catch (error) {
        if (seq === this.requestSeq) this.overview = null;
      } finally {
        if (seq === this.requestSeq) this.loadingOverview = false;
      }
    },

    submit() {
      if (this.saving || !this.$refs.form.validate()) return;
      const f = this.form;
      const [from, to] = f.range || [];
      const periodText = from && to ? `${this.localeDate(from)} - ${this.localeDate(to)}` : undefined;
      const reason =
        this.isPayout && this.amountDiffers && f.reason ? `${this.$t("finance_difference_reason")}: ${f.reason}` : "";
      const note = [f.reference, reason].filter(Boolean).join(" | ") || undefined;

      const payload = {
        amount: Number(f.amount),
        date: f.date,
        period: periodText,
        method: f.method,
        note,
        idempotency_key: this.idempotencyKey,
      };
      if (from && to) {
        payload.period_from = from;
        payload.period_to = to;
      }
      if (this.isPayout) {
        // Zadržano radi kompatibilnosti sa starom validacijom /payouts (turnover/deductions/net).
        payload.turnover = this.calc.turnover ?? 0;
        payload.deductions = this.calc.deductions ?? 0;
        payload.net = this.calc.net ?? Number(f.amount);
      }
      this.$emit("save", payload);
    },
  },
};
</script>

<style scoped>
.rp-box {
  background: #f3f3f1;
  border-radius: 8px;
  padding: 12px 14px;
}

.rp-grid {
  display: grid;
  grid-template-columns: 1.3fr 1fr 1fr;
  gap: 8px 16px;
}

.rp-label {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
}

.rp-big {
  font-size: 1.35rem;
  font-weight: 800;
}

.rp-val {
  font-weight: 600;
}

.rp-link {
  display: inline-block;
  margin-top: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}

.rp-warning {
  background: #fff4e0;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 0.8rem;
  color: #7a5200;
}
</style>
